import { AI_SEARCH_PLATFORM_SPOKES } from "../content/entries/ai-search-platforms";
import { hasAdultTopicFaqs } from "../content/editorial-sections";
import { ALL_APP_LOCALES, DEFAULT_LOCALE } from "../i18n/constants";
import { INDEXABLE_LOCALES } from "../seo/indexing";
import type { RouteDefinition, RouteDeviceSurface, RouteScope, RouteSchemaType } from "./types";

const SHADOW_MODE_NOTE =
  "Registry-backed SEO route metadata. Use through central helpers for sitemap, metadata, AMP links, and route QA.";
const CANONICAL_ROUTE_INDEXABLE = INDEXABLE_LOCALES.includes(DEFAULT_LOCALE);
const PUBLIC_PERSONA_HUB_NOTE =
  "Owner-approved public persona hub. It is indexable, sitemap-included, localized, and canonical-only until a persona AMP adapter ships.";
const PUBLIC_PERSONA_HUB_AMP_NOTE =
  "Owner-approved public persona hub. It is indexable, sitemap-included, localized, and paired with a live AMP route.";
const PUBLIC_PERSONA_SERVICE_NOTE =
  "Owner-approved public persona service route. It is indexable, sitemap-included, localized, and canonical-only until a persona AMP adapter ships.";
const PUBLIC_PERSONA_INDUSTRY_FACET_NOTE =
  "Owner-approved public persona industry facet. It is indexable, sitemap-included, localized, and canonical-only until a persona AMP adapter ships.";
const PUBLIC_PERSONA_PATH_NOTE =
  "Owner-approved public persona buyer path. It is indexable, sitemap-included, localized, and canonical-only until a persona AMP adapter ships.";
const PUBLIC_PERSONA_PATH_INDEX_NOTE =
  "Owner-approved public persona path index. It is indexable, sitemap-included, localized, and canonical-only until a persona AMP adapter ships.";
const FUNNEL_PLANNER_ROUTE_PATH = "/funnel-planner";
const AI_SEARCH_ARTICLE_PATHS = new Set(
  AI_SEARCH_PLATFORM_SPOKES.filter((spoke) => spoke.schemaType === "article").map((spoke) => spoke.path),
);

const PUBLIC_CANONICAL_DEVICE_SURFACES = [
  "desktop",
  "tablet",
  "mobile",
  "tv",
  "4k",
  "watch",
  "ereader",
  "car",
  "xr",
  "foldable",
  "smart-display",
  "console",
] as const satisfies readonly RouteDeviceSurface[];
const PUBLIC_CANONICAL_AMP_DEVICE_SURFACES = [...PUBLIC_CANONICAL_DEVICE_SURFACES, "amp"] as const satisfies readonly RouteDeviceSurface[];

export const STAGED_PERSONA_HUBS = [
  { id: "agency", path: "/agencies" },
  { id: "franchise", path: "/franchise" },
  { id: "creator", path: "/creators" },
  { id: "adult", path: "/adult" },
  { id: "enterprise", path: "/enterprise" },
] as const;

const STAGED_PERSONA_HUB_PATHS = new Set<string>(STAGED_PERSONA_HUBS.map((hub) => hub.path));
const STAGED_FILESYSTEM_PERSONA_HUBS = STAGED_PERSONA_HUBS.filter((hub) => hub.path !== "/adult");

export const STAGED_PERSONA_SERVICE_ROUTES = [
  { id: "agency", path: "/agencies/agency-white-label", contentKey: "persona-service:agency:agency-white-label" },
  { id: "agency", path: "/agencies/white-label-seo", contentKey: "persona-service:agency:white-label-seo" },
  { id: "agency", path: "/agencies/geo-aeo-ai-search", contentKey: "persona-service:agency:geo-aeo-ai-search" },
  { id: "franchise", path: "/franchise/franchise-multi-location", contentKey: "persona-service:franchise:franchise-multi-location" },
  { id: "franchise", path: "/franchise/ppc-performance", contentKey: "persona-service:franchise:ppc-performance" },
  { id: "creator", path: "/creators/ai-video-production", contentKey: "persona-service:creator:ai-video-production" },
  { id: "creator", path: "/creators/ai-avatar-marketing", contentKey: "persona-service:creator:ai-avatar-marketing" },
  { id: "adult", path: "/adult/adult-industry-marketing", contentKey: "persona-service:adult:adult-industry-marketing" },
  { id: "enterprise", path: "/enterprise/geo-aeo-ai-search", contentKey: "persona-service:enterprise:geo-aeo-ai-search" },
  { id: "enterprise", path: "/enterprise/reputation-management", contentKey: "persona-service:enterprise:reputation-management" },
] as const;

export const STAGED_PERSONA_INDUSTRY_FACET_ROUTES = [
  { id: "agency", path: "/agencies/industries/ecommerce-consumer-brands", contentKey: "persona-industry:agency:ecommerce-consumer-brands" },
  { id: "agency", path: "/agencies/industries/media-publishers", contentKey: "persona-industry:agency:media-publishers" },
  { id: "agency", path: "/agencies/industries/saas-tech", contentKey: "persona-industry:agency:saas-tech" },
  { id: "franchise", path: "/franchise/industries/hospitality-events-travel", contentKey: "persona-industry:franchise:hospitality-events-travel" },
  { id: "franchise", path: "/franchise/industries/education", contentKey: "persona-industry:franchise:education" },
  { id: "creator", path: "/creators/industries/creator-economy", contentKey: "persona-industry:creator:creator-economy" },
  { id: "creator", path: "/creators/industries/media-publishers", contentKey: "persona-industry:creator:media-publishers" },
  { id: "adult", path: "/adult/industries/media-publishers", contentKey: "persona-industry:adult:media-publishers" },
  { id: "enterprise", path: "/enterprise/industries/saas-tech", contentKey: "persona-industry:enterprise:saas-tech" },
  { id: "enterprise", path: "/enterprise/industries/industrial-distribution", contentKey: "persona-industry:enterprise:industrial-distribution" },
  { id: "enterprise", path: "/enterprise/industries/professional-services", contentKey: "persona-industry:enterprise:professional-services" },
] as const;

export const PERSONA_PATH_INDEX_ROUTE = {
  id: "persona-path-index",
  path: "/persona-paths",
  contentKey: "persona-path-index",
} as const;

export const STAGED_PERSONA_PATH_ROUTES = [
  { id: "agency", path: "/agencies/paths/scaling-seo-agency-founder", contentKey: "persona-path:agency:scaling-seo-agency-founder" },
  { id: "agency", path: "/agencies/paths/white-label-seo-reseller", contentKey: "persona-path:agency:white-label-seo-reseller" },
  { id: "agency", path: "/agencies/paths/ppc-performance-agency", contentKey: "persona-path:agency:ppc-performance-agency" },
  { id: "agency", path: "/agencies/paths/web-ux-accessibility-agency", contentKey: "persona-path:agency:web-ux-accessibility-agency" },
  { id: "agency", path: "/agencies/paths/prompt-ai-content-agency", contentKey: "persona-path:agency:prompt-ai-content-agency" },
  { id: "agency", path: "/agencies/paths/multilingual-geo-reseller", contentKey: "persona-path:agency:multilingual-geo-reseller" },
  { id: "agency", path: "/agencies/paths/ai-video-avatar-studio", contentKey: "persona-path:agency:ai-video-avatar-studio" },
  { id: "franchise", path: "/franchise/paths/multi-location-marketing-leader", contentKey: "persona-path:franchise:multi-location-marketing-leader" },
  { id: "franchise", path: "/franchise/paths/franchise-geo-strategist", contentKey: "persona-path:franchise:franchise-geo-strategist" },
  { id: "franchise", path: "/franchise/paths/multi-location-reputation-operator", contentKey: "persona-path:franchise:multi-location-reputation-operator" },
  { id: "creator", path: "/creators/paths/creator-economy-growth-manager", contentKey: "persona-path:creator:creator-economy-growth-manager" },
  { id: "creator", path: "/creators/paths/creator-ai-video-content-team", contentKey: "persona-path:creator:creator-ai-video-content-team" },
  { id: "adult", path: "/adult/paths/lawful-adult-creator-agency", contentKey: "persona-path:adult:lawful-adult-creator-agency" },
  { id: "adult", path: "/adult/paths/adult-platform-compliance-marketing", contentKey: "persona-path:adult:adult-platform-compliance-marketing" },
  { id: "enterprise", path: "/enterprise/paths/regulated-ai-visibility-governance", contentKey: "persona-path:enterprise:regulated-ai-visibility-governance" },
  { id: "enterprise", path: "/enterprise/paths/enterprise-orm-compliance-advisor", contentKey: "persona-path:enterprise:enterprise-orm-compliance-advisor" },
] as const;

const PHASE_0_CANONICAL_PATH_LIST = `
/
/insights
/services
/about
/contact
/support
/portfolio
/packages
/industries/creator-economy
/industries/ecommerce-consumer-brands
/industries/health-wellness
/industries/hospitality-events-travel
/industries/education
/industries/saas-tech
/industries/media-publishers
/industries/industrial-distribution
/industries/professional-services
/industries/ecommerce-consumer-brands/ecommerce-seo
/industries/ecommerce-consumer-brands/product-page-seo
/industries/ecommerce-consumer-brands/category-architecture
/industries/ecommerce-consumer-brands/faceted-navigation-seo
/industries/ecommerce-consumer-brands/product-schema
/industries/ecommerce-consumer-brands/ai-search-product-queries
/industries/health-wellness/health-seo
/industries/health-wellness/ymyl-content-review
/industries/health-wellness/medical-schema
/industries/health-wellness/eeat-health-content
/industries/health-wellness/regulated-claims-review
/industries/health-wellness/health-local-visibility
/industries/media-publishers/publisher-seo
/industries/media-publishers/publisher-topic-authority
/industries/media-publishers/content-velocity-system
/industries/media-publishers/ai-overviews-citation-readiness
/industries/media-publishers/monetization-aware-seo
/industries/media-publishers/publisher-technical-seo
/privacy
/cancellation
/cookies
/acceptable-use
/terms
/accessibility
/editorial-policy
/services/white-label-seo
/services/ppc-performance
/services/reputation-management
/services/geo-aeo-ai-search
/services/ai-avatar-marketing
/services/custom-lora-character-models
/services/ai-video-production
/services/adult-industry-marketing
/services/franchise-multi-location
/services/multilingual-ai-search-localization
/services/ai-search-governance-compliance
/services/agency-white-label
/geo-aeo-ai-search
/ai-avatar-video
/ai-avatar-marketing
/ai-video-production
/adult
/white-label
/franchise-multi-location
/case-studies
/resources
/resources/reporting-cadence
/resources/onboarding-checklist
/resources/compliance-posture
/resources/2026-white-label-agency-playbook
/resources/geo-aeo-readiness-checklist
/resources/ai-overviews-visibility-audit-template
/resources/franchise-seo-multi-location-reporting-template
/resources/entity-schema-implementation-guide
/resources/ai-citation-tracking-template
/resources/multi-location-local-seo-audit-template
/resources/ecommerce-seo-audit-template
/resources/health-wellness-seo-compliance-checklist
/resources/b2b-saas-geo-aeo-visibility-audit
/resources/hospitality-travel-seo-playbook
/resources/18-plus-compliance-marketing-checklist
/resources/fan-platform-growth-playbook
/resources/ai-video-cost-time-calculator
/resources/senior-solo-seo-hiring-brief
/resources/white-label-margin-calculator
/ai-search/what-is-geo
/ai-search/what-is-aeo
/ai-search/geo-vs-seo
/ai-search/geo-aeo-checklist
/ai-search/ai-citation-building-101
/ai-search/entity-optimization-ai-search
/ai-search/schema-for-ai-engines
/ai-search/zero-click-search-strategy
/ai-search/ai-ready-content-frameworks
/ai-search/measuring-ai-citation-success
/ai-search/google-ai-overviews-optimization
/ai-search/chatgpt-seo
/ai-search/perplexity-optimization
/ai-search/gemini-optimization
/ai-search/bing-copilot-optimization
/adult/adult-seo
/adult/adult-ai-search-optimization
/adult/fan-platform-marketing
/adult/creator-agency-services
/adult/compliance
/adult/age-verification
/adult/performer-consent
/adult/payment-processor
/adult/platform-policy
/adult/aup-explainer
/adult/adult-reputation-management
/adult/mainstream-platform-brand-safe-strategy
/adult/onlyfans-marketing
/adult/fanvue-growth
/adult/fansly-strategy
/adult/pornhub-seo
/adult/reddit-promotion
/adult/tiktok-adult-safe
/adult/instagram-adult-safe
/adult/youtube-shorts-adult-safe
/compare/vs-generic-white-label-seo
/compare/vs-ai-content-tools
/compare/vs-freelancer-marketplaces
/blog-detail/42
/blog-detail/41
/blog-detail/40
/blog-detail/39
/blog-detail/38
/blog-detail/37
/blog-detail/36
/blog-detail/35
/blog-detail/34
/funnel-planner
`;

export const RESERVED_ROUTE_SEGMENTS = [
  "about",
  "acceptable-use",
  "accessibility",
  "adult",
  "ai-avatar-marketing",
  "ai-avatar-video",
  "ai-search",
  "ai-video-production",
  "amp",
  "api",
  "app",
  "blog",
  "blog-detail",
  "blogs",
  "case-studies",
  "checkout",
  "client-portal",
  "compare",
  "contact",
  "cancellation",
  "cookies",
  "data-deletion",
  "editorial-policy",
  "franchise-multi-location",
  "funnel-planner",
  "geo-aeo-ai-search",
  "industries",
  "insights",
  "offline",
  "packages",
  "paths",
  "persona-paths",
  "portfolio",
  "privacy",
  "privacy-policy",
  "resources",
  "services",
  "share-audit",
  "support",
  "terms",
  "white-label",
] as const;

export const PHASE_0_CANONICAL_PATHS = PHASE_0_CANONICAL_PATH_LIST.trim().split(/\s+/);

export const ACTIVE_PERSONA_LEGACY_REDIRECTS = {
  "/white-label": "/agencies",
  "/franchise-multi-location": "/franchise",
  "/industries/creator-economy": "/creators",
} as const;

const ACTIVE_PERSONA_LEGACY_REDIRECT_PATHS = new Set<string>(Object.keys(ACTIVE_PERSONA_LEGACY_REDIRECTS));
const INTERNAL_REDIRECT_ALIASES = {
  ...ACTIVE_PERSONA_LEGACY_REDIRECTS,
  "/blog": "/insights",
  "/blogs": "/insights",
  "/privacy-policy": "/privacy",
  // External platform reviews (e.g. the Meta app configuration) ask for a "data deletion URL".
  // /cancellation is that surface ("Cancel services or request data deletion"), so /data-deletion
  // is an alias only: a 301 to the localized cancellation path, never a second indexable page.
  "/data-deletion": "/cancellation",
  "/services/seo": "/services/white-label-seo",
  "/services/c2pa-content-credentials-readiness": "/services",
  "/resources/margin-calculator": "/resources/white-label-margin-calculator",
  "/adult/adult-geo-aeo": "/adult/adult-ai-search-optimization",
  "/adult/adult-orm": "/adult/adult-reputation-management",
  "/adult/compliance-policy": "/adult/compliance",
} as const;

const REDIRECT_AND_FILESYSTEM_ROUTES: readonly RouteDefinition[] = [
  {
    key: "pwa-share-audit-receiver",
    path: "/share-audit",
    kind: "filesystem-only",
    scope: "utility",
    source: "filesystem",
    indexable: false,
    localeEligibility: "all-active-locales",
    locales: ALL_APP_LOCALES,
    amp: { advertised: false, current: false },
    devices: PUBLIC_CANONICAL_DEVICE_SURFACES,
    schemaTypes: ["WebPage"],
    reservedSegments: reservedSegmentsForPath("/share-audit"),
    notes: "Noindex installed-PWA share receiver. Shared values stay browser-local behind a short-lived nonce and are never fetched or submitted.",
  },
  {
    key: "pwa-offline-fallback",
    path: "/offline",
    kind: "filesystem-only",
    scope: "utility",
    source: "filesystem",
    contentKey: "pwa:offline-update",
    indexable: false,
    localeEligibility: "all-active-locales",
    locales: ALL_APP_LOCALES,
    amp: { advertised: false, current: false },
    devices: PUBLIC_CANONICAL_DEVICE_SURFACES,
    schemaTypes: ["WebPage"],
    reservedSegments: reservedSegmentsForPath("/offline"),
    notes: "Noindex localized public fallback used only when a safe same-origin navigation cannot reach the network.",
  },
  {
    key: "manual-transfer-checkout",
    path: "/checkout/manual-transfer",
    kind: "filesystem-only",
    scope: "utility",
    source: "filesystem",
    contentKey: "app-layer:manual-transfer-checkout",
    indexable: false,
    localeEligibility: "all-active-locales",
    locales: ALL_APP_LOCALES,
    amp: { advertised: false, current: false },
    devices: PUBLIC_CANONICAL_DEVICE_SURFACES,
    schemaTypes: ["WebPage", "BreadcrumbList"],
    reservedSegments: reservedSegmentsForPath("/checkout/manual-transfer"),
    notes:
      "Noindex, sitemap-excluded request-quote explanation. Bank details and the transfer-report form require an expiring owner-signed one-time grant.",
  },
  {
    key: "client-portal-app-surface",
    path: "/client-portal",
    kind: "filesystem-only",
    scope: "utility",
    source: "filesystem",
    indexable: false,
    localeEligibility: "all-active-locales",
    locales: ALL_APP_LOCALES,
    amp: { advertised: false, current: false },
    devices: PUBLIC_CANONICAL_DEVICE_SURFACES,
    schemaTypes: ["WebPage"],
    reservedSegments: reservedSegmentsForPath("/client-portal"),
    notes: "Noindex invite-only client portal app surface. Excluded from canonical sitemap, AMP, and dynamic-serving cache path generation.",
  },
  {
    key: "client-mcp-oauth-consent",
    path: "/client-mcp/oauth/consent",
    kind: "filesystem-only",
    scope: "utility",
    source: "filesystem",
    contentKey: "app-layer:client-mcp-oauth-consent",
    indexable: false,
    localeEligibility: "all-active-locales",
    locales: ALL_APP_LOCALES,
    amp: { advertised: false, current: false },
    devices: PUBLIC_CANONICAL_DEVICE_SURFACES,
    schemaTypes: ["WebPage"],
    reservedSegments: reservedSegmentsForPath("/client-mcp/oauth/consent"),
    notes:
      "Noindex authenticated Supabase OAuth consent surface. It preserves authorization_id, displays exact client/scopes/account, and never appears in sitemap or AMP.",
  },
  filesystemPersonaPathIndexRoute(PERSONA_PATH_INDEX_ROUTE),
  ...STAGED_FILESYSTEM_PERSONA_HUBS.map(filesystemPersonaHubRoute),
  ...STAGED_PERSONA_SERVICE_ROUTES.map(filesystemPersonaServiceRoute),
  ...STAGED_PERSONA_INDUSTRY_FACET_ROUTES.map(filesystemPersonaIndustryFacetRoute),
  ...STAGED_PERSONA_PATH_ROUTES.map(filesystemPersonaPathRoute),
  redirectRoute("/blog", "/insights", "blog-alias"),
  redirectRoute("/blogs?page=:page", "/insights", "blogs-pagination-alias"),
  redirectRoute("/app", "/", "app-alias"),
  redirectRoute("/privacy-policy", "/privacy", "privacy-policy-alias"),
  redirectRoute("/data-deletion", "/cancellation", "data-deletion-alias"),
  redirectRoute("/services/seo", "/services/white-label-seo", "service-seo-alias"),
  redirectRoute(
    "/services/c2pa-content-credentials-readiness",
    "/services",
    "c2pa-content-credentials-readiness-retired",
  ),
  redirectRoute("/resources/margin-calculator", "/resources/white-label-margin-calculator", "resource-margin-calculator-alias"),
  redirectRoute("/adult/adult-geo-aeo", "/adult/adult-ai-search-optimization", "adult-geo-aeo-alias"),
  redirectRoute("/adult/adult-orm", "/adult/adult-reputation-management", "adult-orm-alias"),
  redirectRoute("/adult/compliance-policy", "/adult/compliance", "adult-compliance-policy-alias"),
  redirectRoute("/blog-deatail/:id", "/blog-detail/:id", "blog-detail-typo-alias"),
  {
    key: "filesystem-blogs-page-shadowed-by-proxy",
    path: "/blogs",
    kind: "shadowed",
    scope: "blog",
    source: "filesystem",
    redirectTo: "/insights",
    indexable: false,
    localeEligibility: "all-active-locales",
    locales: ALL_APP_LOCALES,
    amp: {
      advertised: true,
      current: false,
      knownGap: "Filesystem page renders an amphtml link, but proxy redirects /blogs to /insights before page render.",
    },
    devices: PUBLIC_CANONICAL_DEVICE_SURFACES,
    schemaTypes: ["WebPage"],
    notes: SHADOW_MODE_NOTE,
  },
];

export const ROUTE_REGISTRY: readonly RouteDefinition[] = [
  ...PHASE_0_CANONICAL_PATHS.map(phase0Route),
  ...REDIRECT_AND_FILESYSTEM_ROUTES,
];

export function getCanonicalRouteDefinitions(): readonly RouteDefinition[] {
  return ROUTE_REGISTRY.filter((route) => route.kind === "canonical");
}

export function getKnownPublicRouteDefinitions(): readonly RouteDefinition[] {
  return ROUTE_REGISTRY.filter((route) => route.kind !== "dead");
}

export function getRedirectRouteDefinitions(): readonly RouteDefinition[] {
  return ROUTE_REGISTRY.filter((route) => route.kind !== "canonical");
}

export function canonicalRoutePathForInternalLink(path = "/"): string {
  const [pathname] = path.trim().split(/[?#]/, 1);
  const normalizedPath = pathname?.startsWith("/") ? pathname : `/${pathname || ""}`;
  const withoutTrailingSlash = normalizedPath.length > 1 ? normalizedPath.replace(/\/+$/, "") : normalizedPath;
  const stablePath = withoutTrailingSlash || "/";

  if (stablePath.startsWith("/blog-deatail/")) {
    return stablePath.replace("/blog-deatail/", "/blog-detail/");
  }

  return INTERNAL_REDIRECT_ALIASES[stablePath as keyof typeof INTERNAL_REDIRECT_ALIASES] ?? stablePath;
}

export function getStagedPersonaHubRouteDefinitions(): readonly RouteDefinition[] {
  return ROUTE_REGISTRY.filter((route) => route.scope === "persona" && route.contentKey?.startsWith("persona-hub:"));
}

export function getStagedPersonaServiceRouteDefinitions(): readonly RouteDefinition[] {
  return ROUTE_REGISTRY.filter((route) => route.scope === "persona" && route.contentKey?.startsWith("persona-service:"));
}

export function getStagedPersonaIndustryFacetRouteDefinitions(): readonly RouteDefinition[] {
  return ROUTE_REGISTRY.filter((route) => route.scope === "persona" && route.contentKey?.startsWith("persona-industry:"));
}

export function getStagedPersonaPathRouteDefinitions(): readonly RouteDefinition[] {
  return ROUTE_REGISTRY.filter((route) => route.scope === "persona" && route.contentKey?.startsWith("persona-path:"));
}

export function getCurrentAmpRouteDefinitions(): readonly RouteDefinition[] {
  return getCanonicalRouteDefinitions().filter((route) => route.amp.current);
}

export function getAdvertisedAmpGapRouteDefinitions(): readonly RouteDefinition[] {
  return getCanonicalRouteDefinitions().filter((route) => route.amp.advertised && !route.amp.current);
}

export function isReservedRouteSegment(segment: string): boolean {
  return RESERVED_ROUTE_SEGMENTS.includes(segment.toLowerCase() as (typeof RESERVED_ROUTE_SEGMENTS)[number]);
}

export function findReservedRouteSegmentCollisions(slugs: readonly string[]): string[] {
  return slugs.filter(isReservedRouteSegment);
}

function canonicalRoute(path: string): RouteDefinition {
  const scope = scopeForPath(path);
  const amp = ampSupportForPath(path);
  const stagedPersonaHub = personaHubForPath(path);

  return {
    key: routeKeyForPath(path),
    path,
    kind: "canonical",
    scope,
    source: "phase-0-sitemap",
    contentKey: contentKeyForPath(path, scope),
    indexable: CANONICAL_ROUTE_INDEXABLE,
    localeEligibility: "all-active-locales",
    locales: ALL_APP_LOCALES,
    amp,
    devices: amp.current ? PUBLIC_CANONICAL_AMP_DEVICE_SURFACES : PUBLIC_CANONICAL_DEVICE_SURFACES,
    schemaTypes: schemaTypesForScope(scope, path),
    reservedSegments: reservedSegmentsForPath(path),
    notes: stagedPersonaHub ? (amp.current ? PUBLIC_PERSONA_HUB_AMP_NOTE : PUBLIC_PERSONA_HUB_NOTE) : SHADOW_MODE_NOTE,
  };
}

function phase0Route(path: string): RouteDefinition {
  const redirectTo = ACTIVE_PERSONA_LEGACY_REDIRECTS[path as keyof typeof ACTIVE_PERSONA_LEGACY_REDIRECTS];

  if (redirectTo) {
    return redirectRoute(path, redirectTo, `persona-legacy-${routeKeyForPath(path)}`);
  }

  return canonicalRoute(path);
}

function filesystemPersonaHubRoute(hub: (typeof STAGED_FILESYSTEM_PERSONA_HUBS)[number]): RouteDefinition {
  return {
    key: `persona-${hub.id}-hub`,
    path: hub.path,
    kind: "canonical",
    scope: "persona",
    source: "filesystem",
    contentKey: `persona-hub:${hub.id}`,
    indexable: CANONICAL_ROUTE_INDEXABLE,
    localeEligibility: "indexable-locales",
    locales: ALL_APP_LOCALES,
    amp: { advertised: false, current: false },
    devices: PUBLIC_CANONICAL_DEVICE_SURFACES,
    schemaTypes: ["WebPage", "BreadcrumbList", "FAQPage"],
    reservedSegments: reservedSegmentsForPath(hub.path),
    notes: PUBLIC_PERSONA_HUB_NOTE,
  };
}

function filesystemPersonaServiceRoute(route: (typeof STAGED_PERSONA_SERVICE_ROUTES)[number]): RouteDefinition {
  return {
    key: `persona-${route.id}-${route.path.split("/").at(-1)}`,
    path: route.path,
    kind: "canonical",
    scope: "persona",
    source: "filesystem",
    contentKey: route.contentKey,
    indexable: CANONICAL_ROUTE_INDEXABLE,
    localeEligibility: "indexable-locales",
    locales: ALL_APP_LOCALES,
    amp: { advertised: false, current: false },
    devices: PUBLIC_CANONICAL_DEVICE_SURFACES,
    schemaTypes: ["WebPage", "BreadcrumbList", "Service", "FAQPage"],
    reservedSegments: reservedSegmentsForPath(route.path),
    notes: PUBLIC_PERSONA_SERVICE_NOTE,
  };
}

function filesystemPersonaIndustryFacetRoute(route: (typeof STAGED_PERSONA_INDUSTRY_FACET_ROUTES)[number]): RouteDefinition {
  return {
    key: `persona-${route.id}-industry-${route.path.split("/").at(-1)}`,
    path: route.path,
    kind: "canonical",
    scope: "persona",
    source: "filesystem",
    contentKey: route.contentKey,
    indexable: CANONICAL_ROUTE_INDEXABLE,
    localeEligibility: "indexable-locales",
    locales: ALL_APP_LOCALES,
    amp: { advertised: false, current: false },
    devices: PUBLIC_CANONICAL_DEVICE_SURFACES,
    schemaTypes: ["WebPage", "BreadcrumbList", "FAQPage"],
    reservedSegments: reservedSegmentsForPath(route.path),
    notes: PUBLIC_PERSONA_INDUSTRY_FACET_NOTE,
  };
}

function filesystemPersonaPathIndexRoute(route: typeof PERSONA_PATH_INDEX_ROUTE): RouteDefinition {
  return {
    key: route.id,
    path: route.path,
    kind: "canonical",
    scope: "persona",
    source: "filesystem",
    contentKey: route.contentKey,
    indexable: CANONICAL_ROUTE_INDEXABLE,
    localeEligibility: "indexable-locales",
    locales: ALL_APP_LOCALES,
    amp: { advertised: false, current: false },
    devices: PUBLIC_CANONICAL_DEVICE_SURFACES,
    schemaTypes: ["ItemList", "WebPage", "BreadcrumbList"],
    reservedSegments: reservedSegmentsForPath(route.path),
    notes: PUBLIC_PERSONA_PATH_INDEX_NOTE,
  };
}

function filesystemPersonaPathRoute(route: (typeof STAGED_PERSONA_PATH_ROUTES)[number]): RouteDefinition {
  return {
    key: `persona-${route.id}-path-${route.path.split("/").at(-1)}`,
    path: route.path,
    kind: "canonical",
    scope: "persona",
    source: "filesystem",
    contentKey: route.contentKey,
    indexable: CANONICAL_ROUTE_INDEXABLE,
    localeEligibility: "indexable-locales",
    locales: ALL_APP_LOCALES,
    amp: { advertised: false, current: false },
    devices: PUBLIC_CANONICAL_DEVICE_SURFACES,
    schemaTypes: ["WebPage", "BreadcrumbList", "FAQPage"],
    reservedSegments: reservedSegmentsForPath(route.path),
    notes: PUBLIC_PERSONA_PATH_NOTE,
  };
}

function redirectRoute(path: string, redirectTo: string, key: string): RouteDefinition {
  return {
    key,
    path,
    kind: "redirect",
    scope: scopeForPath(path),
    source: "proxy",
    redirectTo,
    indexable: false,
    localeEligibility: "all-active-locales",
    locales: ALL_APP_LOCALES,
    amp: { advertised: false, current: false },
    devices: PUBLIC_CANONICAL_DEVICE_SURFACES,
    schemaTypes: ["WebPage"],
    reservedSegments: reservedSegmentsForPath(path),
    notes: SHADOW_MODE_NOTE,
  };
}

function routeKeyForPath(path: string): string {
  const stagedPersonaHub = personaHubForPath(path);

  if (stagedPersonaHub) {
    return `persona-${stagedPersonaHub.id}-hub`;
  }

  return path === "/" ? "home" : path.replace(/^\//, "").replace(/[^a-z0-9]+/g, "-");
}

function contentKeyForPath(path: string, scope: RouteScope): string {
  const stagedPersonaHub = personaHubForPath(path);

  if (path === FUNNEL_PLANNER_ROUTE_PATH) {
    return "funnel-planner";
  }

  if (scope === "persona" && stagedPersonaHub) {
    return `persona-hub:${stagedPersonaHub.id}`;
  }

  return `${scope}:${path === "/" ? "home" : path.replace(/^\//, "")}`;
}

function scopeForPath(path: string): RouteScope {
  if (ACTIVE_PERSONA_LEGACY_REDIRECT_PATHS.has(path)) return "persona";
  if (isStagedPersonaHubPath(path)) return "persona";
  if (path === "/" || path === "/about" || path === "/contact") return "global";
  if (
    path === "/privacy" ||
    path === "/cancellation" ||
    path === "/cookies" ||
    path === "/editorial-policy" ||
    path === "/acceptable-use" ||
    path === "/terms" ||
    path === "/accessibility"
  ) {
    return "legal";
  }
  if (path === "/services" || path.startsWith("/services/")) return "service";
  if (path === "/industries/creator-economy") return "industry";
  if (path.startsWith("/industries/") && path.split("/").length > 3) return "industry-cluster";
  if (path.startsWith("/industries/")) return "industry";
  if (path === "/resources" || path.startsWith("/resources/")) return "resource";
  if (path === FUNNEL_PLANNER_ROUTE_PATH) return "resource";
  if (path === "/insights" || path === "/blog" || path === "/blogs" || path.startsWith("/blog-detail/")) return "blog";
  if (path.startsWith("/compare/")) return "comparison";
  if (path === "/adult" || path.startsWith("/adult/")) return "adult";
  if (path === "/case-studies") return "case-study";
  if (path.startsWith("/ai-search/")) return "ai-search";

  return "utility";
}

function ampSupportForPath(path: string): RouteDefinition["amp"] {
  if (path === "/adult") {
    return { advertised: true, current: true };
  }

  if (isStagedPersonaHubPath(path)) {
    return { advertised: false, current: false };
  }

  if (
    path === "/" ||
    path === "/services" ||
    path.startsWith("/services/") ||
    path === "/insights" ||
    path.startsWith("/blog-detail/") ||
    path === "/privacy" ||
    path === "/terms" ||
    path === "/accessibility" ||
    path === "/about" ||
    path === "/contact" ||
    path === "/resources" ||
    path === "/case-studies" ||
    path === "/industries/creator-economy" ||
    path === "/white-label" ||
    path === "/geo-aeo-ai-search" ||
    path === "/ai-avatar-video" ||
    path === "/ai-avatar-marketing" ||
    path === "/ai-video-production" ||
    path.startsWith("/adult/") ||
    path === "/franchise-multi-location"
  ) {
    return { advertised: true, current: true };
  }

  if (
    path.startsWith("/resources/") ||
    path.startsWith("/industries/") ||
    path.startsWith("/ai-search/") ||
    path.startsWith("/compare/") ||
    path === "/cookies" ||
    path === "/acceptable-use" ||
    path === "/portfolio" ||
    path === "/packages"
  ) {
    return {
      advertised: true,
      current: false,
      knownGap: "Canonical page advertises amphtml before AMP route support exists for this content type.",
    };
  }

  return { advertised: false, current: false };
}

function schemaTypesForScope(scope: RouteScope, path: string): RouteSchemaType[] {
  if (path === "/") return ["Organization", "WebSite", "WebPage"];
  if (path === FUNNEL_PLANNER_ROUTE_PATH) return ["WebPage", "BreadcrumbList"];
  if (path === "/terms" || path === "/accessibility") return ["WebPage", "BreadcrumbList"];
  if (path === "/resources") return ["WebPage", "BreadcrumbList"];
  if (path === "/case-studies") return ["WebPage", "BreadcrumbList"];
  if (
    path === "/geo-aeo-ai-search" ||
    path === "/ai-avatar-video" ||
    path === "/ai-avatar-marketing" ||
    path === "/ai-video-production" ||
    path === "/franchise-multi-location"
  ) {
    return ["WebPage", "BreadcrumbList", "Service", "FAQPage"];
  }
  if (scope === "persona") return ["WebPage", "BreadcrumbList", "FAQPage"];
  if (scope === "blog") return path.startsWith("/blog-detail/") ? ["WebPage", "BreadcrumbList", "BlogPosting"] : ["ItemList", "WebPage"];
  if (scope === "service") return ["WebPage", "BreadcrumbList", "Service", "FAQPage"];
  if (scope === "resource") return ["WebPage", "BreadcrumbList", "CreativeWork"];
  if (scope === "industry" || scope === "industry-cluster") return ["WebPage", "BreadcrumbList", "Service", "FAQPage"];
  if (scope === "adult") return hasAdultTopicFaqs(path.split("/").at(-1) ?? "")
    ? ["WebPage", "BreadcrumbList", "Service", "FAQPage"]
    : ["WebPage", "BreadcrumbList", "Service"];
  if (scope === "comparison") return ["WebPage", "BreadcrumbList", "Service"];
  if (scope === "ai-search") {
    return AI_SEARCH_ARTICLE_PATHS.has(path)
      ? ["WebPage", "BreadcrumbList", "Article", "DefinedTerm", "FAQPage"]
      : ["WebPage", "BreadcrumbList", "Service", "FAQPage"];
  }

  return ["WebPage"];
}

function isStagedPersonaHubPath(path: string): boolean {
  return STAGED_PERSONA_HUB_PATHS.has(path);
}

function personaHubForPath(path: string): (typeof STAGED_PERSONA_HUBS)[number] | undefined {
  return STAGED_PERSONA_HUBS.find((hub) => hub.path === path);
}

function reservedSegmentsForPath(path: string): string[] {
  return path
    .split("/")
    .filter(Boolean)
    .filter(isReservedRouteSegment);
}
